import { Download as DownloadIcon, ArrowForwardIos as ArrowForwardIosIcon, InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  FormControl,
  Grid,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  CircularProgress,
  ListSubheader
} from '@mui/material';
import { scaleOrdinal } from 'd3-scale';
import { getLocalStorageId } from '../../authProvider';
import { schemeCategory10 } from 'd3-scale-chromatic';
import { MaterialReactTable, MRT_Cell, MRT_ColumnDef, MRT_SortingState } from 'material-react-table';
import { MRT_Localization_JA } from 'material-react-table/locales/ja';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Title, useDataProvider, useTranslate, useStore, useAddNotificationContext, usePermissions } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ComposedChart,
  Line,
} from 'recharts';
import { ANALYSIS_DATE_TYPE, COUPON_FLAG } from '../constants/choices';
import CustomGraphTooltip from './CustomGraphTooltip';
import { DatePicker } from '@mui/x-date-pickers/DatePicker'; // DatePicker をインポート
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ja';

dayjs.locale('ja');
type TranslateFunction = (key: string, options?: any) => string;
type SummaryKeys = keyof Summary;
interface SummaryItemProps {
  label: string;
  value: number | undefined;
  dataKey: SummaryKeys;
  gridProps?: { [key in 'xs' | 'sm' | 'md' | 'lg' | 'xl']?: number };
  tooltipText?: string;
}

interface DataItem {
  id: string;
  name: string;
  partnerId?: number; // ★追加: 成果アフィリエイトの所有パートナーID
  partnerName: string;
  introducerName: string | null;
  salesAmount: number;
  salesCount: number;
  clickCount: number;
  salesAmountWithCoupon: number;
  salesCountWithCoupon: number;
  feebackCommission: number;
  partnerCommission: number;
  introducerCommission: number;
  affiliateOwnerType: number;
  totalAffiliateCommission?: number;
  pendingPartnerCommission: number;
  pendingIntroducerCommission: number;
  salesAmountRate?: string;
  // lostPartnerCommission: number;
  // lostIntroducerCommission?: number;
}

interface DataEntry {
  date: string;
  items: DataItem[];
}

interface Summary {
  totalSalesAmount: number;
  totalFeebackCommission: number;
  totalPartnerCommission: number;
  totalIntroducerCommission: number;
  totalAffiliateCommission: number;
  totalClickCount: number;
  totalSalesCount: number;
  totalFeebackClaim: number;
}

interface SalesProps {
  selectedResultType?: number;
}

interface ClickableSummaryItemProps {
  label: string;
  value: number | undefined;
  onClick: () => void;
  gridProps?: { [key in 'xs' | 'sm']?: number };
  /**
   * 値の後ろに表示する単位の i18n キー。未指定時は `values.graphTargetUnits.salesAmount`（"円"）。
   */
  unitTranslationKey?: string;
}
const ClickableSummaryItem: React.FC<ClickableSummaryItemProps> = ({ label, value, onClick, gridProps, unitTranslationKey }) => {
  const translate = useTranslate();

  return (
    <Grid item {...gridProps}>
      <Box>
        <Typography
          variant="caption"
          sx={{
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            display: 'block',
            fontSize: { md: '1rem' },
            minHeight: { xs: '2.5em', md: 'auto' },
            marginBottom: '0'
          }}
        >
          {label}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          onClick={onClick}
          sx={{ cursor: 'pointer', display: 'inline-flex' }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '1rem', md: '1.5rem' }
            }}
          >
            {typeof value === 'number' ? formatNumber(value) : '-'}
            {typeof value === 'number' && (
              <Box component="span" sx={{ fontSize: '0.8em', ml: 0.5 }}>
                {translate(unitTranslationKey ?? 'values.graphTargetUnits.salesAmount')}
              </Box>
            )}
          </Typography>
          <ArrowForwardIosIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
        </Stack>
      </Box>
    </Grid>
  );
};

const customLocalization = {
  ...MRT_Localization_JA,
  showHideColumns: '列の表示/非表示',
  showAllColumns: '全て表示',
  hideAllColumns: '全て非表示'
};

const formatNumber = (num: number): string => {
  return Math.floor(num).toLocaleString(); // 小数点以下を切り捨て
};

const integerNumber = (num: number): number => {
  return Math.floor(num);
};

const formatDate = (date: string, dateType: number | undefined): string => {
  const [year, month, day] = date.split('-');
  return dateType === 1 ? `${year}-${month}` : `${year}-${month}-${day}`;
};

const CustomTableTooltip: React.FC<{ item: DataItem }> = ({ item }) => {
  const translate = useTranslate();
  const affiliateDetails =
    item.affiliateOwnerType === 3
      ? [{ partnerName: item.partnerName, introducerCommission: item.introducerCommission }]
      : [];

  return (
    <div>
      <Typography variant="body2">
        {`内${translate('resources.sales.fields.partnerCommission')}: ${formatNumber(
          item.partnerCommission
        )}${translate('values.graphTargetUnits.partnerCommission')}`}
      </Typography>
      {affiliateDetails.map((detail, index) => (
        <Typography key={index} variant="body2">
          {`内${translate('resources.sales.fields.introducerCommission')}:`}
          <br />
          {`${detail.partnerName} : ${formatNumber(detail.introducerCommission)}${translate(
            'values.graphTargetUnits.introducerCommission'
          )}`}
        </Typography>
      ))}
    </div>
  );
};

// 単位を取得するためのヘルパー関数
const getUnitForSummary = (key: SummaryKeys, translate: TranslateFunction): string => {
  if (key.includes('Amount') || key.includes('Commission') || key.includes('Claim')) {
    return translate('values.graphTargetUnits.salesAmount');
  }
  if (key.includes('Count')) {
    return translate('values.graphTargetUnits.salesCount');
  }
  return '';
};

// タッチ中のみ表示するツールチップコンポーネント
const TouchTooltipIcon = ({ title, fontSize = '1rem' }: { title: string; fontSize?: string }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <MuiTooltip
      title={title}
      open={open}
      placement="top"
      arrow
      disableFocusListener
      disableHoverListener
      disableTouchListener
    >
      <InfoOutlinedIcon
        sx={{
          fontSize: fontSize,
          ml: 0.5,
          color: 'error.main',
          cursor: 'help',
          touchAction: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
        // --- PC用 (ホバー) ---
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        // --- スマホ用 (タッチ中のみ表示) ---
        onTouchStart={handleOpen}
        onTouchEnd={handleClose}
        // クリックイベントの伝播を止める
        onClick={(e) => e.stopPropagation()}
      />
    </MuiTooltip>
  );
};

// サマリーの各項目を表示するコンポーネント
const SummaryItem: React.FC<SummaryItemProps> = ({ label, value, dataKey, gridProps, tooltipText }) => {
  const translate = useTranslate();
  // const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md')); // ラベルが長すぎる場合にスマートフォンで改行する処理

  // const formattedLabel = (lbl: string): string => {
  //   if (isSmallScreen && lbl.length > 8) {
  //     return lbl.slice(0, 8) + '\n' + lbl.slice(8);
  //   }
  //   return lbl;
  // }; // 値が未定義の場合はハイフンを表示

  const displayValue = typeof value === 'number' ? formatNumber(value) : '-';

  return (
    // 画面サイズに応じて1行に2〜4項目表示
    <Grid item {...gridProps}>
      <Box>
        <Typography
          variant="caption"
          sx={{
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            display: 'flex',
            alignItems: 'center',
            fontSize: { md: '1rem' },
            minHeight: { xs: '2.5em', md: 'auto' },
            marginBottom: '0'
          }}
        >
          {label}
          {/* tooltipTextがある場合のみアイコンを表示 */}
          {tooltipText && (
            <TouchTooltipIcon title={tooltipText} />
          )}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 'bold',
            // PC表示 (md以上) で文字を大きくする
            fontSize: {
              xs: '1rem', // スマホでの値の文字サイズ (現在のまま)
              md: '1.5rem' // 通常時の値の文字サイズ
            }
          }}
        >
          {displayValue}
          {typeof value === 'number' && ( // 数値の場合のみ単位を表示
            <Box component="span" sx={{ fontSize: '0.8em', ml: 0.5 }}>
              {getUnitForSummary(dataKey, translate)}
            </Box>
          )}
        </Typography>
      </Box>
    </Grid>
  );
};

const Sales: React.FC<SalesProps & { isAdmin?: boolean }> = ({ selectedResultType, isAdmin = true }) => {
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const [data, setData] = useState<DataEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [graphTarget] = useState<string>('salesAmount');
  const storeKeyPrefix = `sales.view[${selectedResultType}]`;
  const [nameFilter, setNameFilter] = useStore<string>(`${storeKeyPrefix}.nameFilter`, '全体');
  const [startDate, setStartDate] = useStore<string>(
    `${storeKeyPrefix}.startDate`,
    new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useStore<string>(`${storeKeyPrefix}.endDate`, new Date().toISOString().split('T')[0]);
  const [dateType, setDateType] = useStore<number>(`${storeKeyPrefix}.dateType`, 2);
  const [couponFlag, setCouponFlag] = useStore<number>(`${storeKeyPrefix}.couponFlag`, 0);
  const [dateError, setDateError] = useState<string | null>(null);
  const [isCompany, setIsCompany] = useState(false);
  const [isShowingAll, setIsShowingAll] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isSmallScreen = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
  const isSmartPhoneScreen = useMediaQuery('(max-width:550px)');
  const colorScale = scaleOrdinal(schemeCategory10);
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const [activeBuyerCount, setActiveBuyerCount] = useState<number | undefined>(undefined);

  type HierarchyPattern =
    | 'all'                   // 自分と2次パートナーの合計
    | 'own'                   // 自分のみの報酬額
    | 'secondary_total'       // 2次パートナーの合計
    | `partner_${number}`;    // 特定の2次パートナー

  const [hierarchyPattern, setHierarchyPattern] = useStore<HierarchyPattern>(
    `${storeKeyPrefix}.hierarchyPattern`,
    'all'
  );

  const hasChildPartners = useMemo(() => {
    return localStorage.getItem('hasChildPartners') === 'true';
  }, []);

  const secondaryPartnersList = useMemo(() => {
    const listMap = new Map<number, string>();
    data.forEach((entry) => {
      entry.items.forEach((item) => {
        if (item.affiliateOwnerType === 3 && item.partnerId && item.partnerName) {
          listMap.set(item.partnerId, item.partnerName);
        }
      });
    });
    return Array.from(listMap.entries()).map(([id, name]) => ({ id, name }));
  }, [data]);

  /**
   * 購入者管理メニュー判定（Req 12.1 / 12.2 / 12.3）と同一の権限で累積顧客数カードを表示。
   * admin (`isAdmin=true`) は ROLE_EDIT または ROLE_SALES、client/partner (`isAdmin=false` or selectedResultType=4) は ROLE_SALES。
   */
  const canSeeBuyerCount = (() => {
    if (!permissions) return false;
    const list = Array.isArray(permissions) ? permissions : [permissions];
    if (isAdmin && selectedResultType !== 4) {
      return list.includes('edit') || list.includes('sales');
    }
    return list.includes('sales');
  })();

  /**
   * 累積顧客数ボタン（カード）を表示するかどうかの判定。
   * 1次パートナーのアナリティクス（selectedResultType === 4）において、
   * パートナー選択で1次パートナー（'own'）以外を選んだ場合は非表示とする。
   * ※ 2次パートナーを持たない場合（!hasChildPartners）はパートナー選択自体が存在しないため常に表示。
   */
  const shouldShowBuyerCount = (() => {
    if (!canSeeBuyerCount) return false;
    if (selectedResultType === 4 && hasChildPartners && hierarchyPattern !== 'own') {
      return false;
    }
    return true;
  })();

  useEffect(() => {
    if (!shouldShowBuyerCount) {
      setActiveBuyerCount(undefined);
      return;
    }

    const fetchBuyerCount = async () => {
      try {
        // 1. 日付調整（月別の場合は月初〜月末）
        let adjustedStartDate = startDate;
        let adjustedEndDate = endDate;
        if (dateType === 1) {
          adjustedStartDate = dayjs(startDate).startOf('month').format('YYYY-MM-DD');
          adjustedEndDate = dayjs(endDate).endOf('month').format('YYYY-MM-DD');
        }

        // 2. フィルタ条件の構築（全画面共通で期間を反映）
        const filterParams: Record<string, any> = {
          startDate: adjustedStartDate,
          endDate: adjustedEndDate
        };

        // 3. 個別選択の連動（Admin EC/パートナー、Client パートナー、Partner 2次パートナー）
        if (selectedResultType === 2 && nameFilter !== '全体') {
          // Admin: ECサイト選択
          filterParams.clientId = Number(nameFilter);
        } else if (selectedResultType === 3 && nameFilter !== '全体') {
          // Admin または Client: パートナー選択
          filterParams.partnerId = Number(nameFilter);
        } else if (selectedResultType === 4) {
          // Partner: パートナー階層セレクター連動
          filterParams.hierarchyPattern = hierarchyPattern;
          if (hierarchyPattern.startsWith('partner_')) {
            const selectedId = hierarchyPattern.replace('partner_', '');
            filterParams.partnerId = Number(selectedId);
          }
        }

        const queryString = encodeURIComponent(JSON.stringify(filterParams));
        const url = `buyers/count?filter=${queryString}`;

        const result = await (dataProvider as any).getData(url);
        if (result && typeof result.activeBuyerCount === 'number') {
          setActiveBuyerCount(result.activeBuyerCount);
        } else {
          setActiveBuyerCount(undefined);
        }
      } catch (error) {
        console.error('Failed to fetch buyers/count:', error);
        setActiveBuyerCount(undefined);
      }
    };

    fetchBuyerCount();
  }, [
    shouldShowBuyerCount,
    dataProvider,
    startDate,
    endDate,
    dateType,
    nameFilter,
    hierarchyPattern,
    selectedResultType,
    isAdmin,
    hasChildPartners
  ]);

  const handleNavigateToBuyers = () => {
    // dateTypeが1（月別）の場合、月初と月末に調整
    let adjustedStartDate = startDate;
    let adjustedEndDate = endDate;
    if (dateType === 1) {
      adjustedStartDate = dayjs(startDate).startOf('month').format('YYYY-MM-DD');
      adjustedEndDate = dayjs(endDate).endOf('month').format('YYYY-MM-DD');
    }

    const filter: Record<string, any> = {
      startDate: adjustedStartDate,
      endDate: adjustedEndDate
    };

    // ECサイトまたはパートナー個別選択時の条件引き継ぎ（Admin/Client/Partner）
    if (selectedResultType === 2 && nameFilter !== '全体') {
      filter.clientId = Number(nameFilter);
    } else if (selectedResultType === 3 && nameFilter !== '全体') {
      filter.partnerId = Number(nameFilter);
    } else if (selectedResultType === 4) {
      filter.hierarchyPattern = hierarchyPattern;
      if (hierarchyPattern.startsWith('partner_')) {
        const selectedId = hierarchyPattern.replace('partner_', '');
        filter.partnerId = Number(selectedId);
      }
    }

    const encodedFilter = encodeURIComponent(JSON.stringify(filter));
    navigate(`/buyers?filter=${encodedFilter}`);
  };

  const handleNavigateToOrders = async () => {
    try {
      let finalStartDate = startDate;
      let finalEndDate = endDate;
      // dateTypeが1（月別）の場合、開始日と終了日を調整
      if (dateType === 1) {
        finalStartDate = dayjs(startDate).startOf('month').format('YYYY-MM-DD');
        finalEndDate = dayjs(endDate).endOf('month').format('YYYY-MM-DD');
      }

      const filter: { [key: string]: any } = {
        approvalStatus: 0,
        orderDate_start: finalStartDate,
        orderDate_end: finalEndDate
      };
      if (couponFlag === 1) {
        filter.hasCoupon = true;
      } else if (couponFlag === 2) {
        filter.hasCoupon = false;
      }

      const displayedFilters: { [key: string]: true } = {};
      if (nameFilter !== '全体') {
        if (selectedResultType === 2) {
          filter.clientId = nameFilter;
          displayedFilters.clientId = true;
        } else if (selectedResultType === 3) {
          const { data: partner } = await dataProvider.getOne('partners', { id: nameFilter });

          if (partner && partner.affiliateId) {
            filter.affiliateId = partner.affiliateId;
            displayedFilters.affiliateId = true;
          } else {
            console.warn(`アフィリエイトIDが見つかりません。パートナーID: ${nameFilter}`);
          }
        }
      }

      if (selectedResultType === 4 && hierarchyPattern) {
        filter.hierarchyPattern = hierarchyPattern;
        displayedFilters.hierarchyPattern = true;
      }

      const encodedFilter = encodeURIComponent(JSON.stringify(filter));
      const encodedDisplayedFilters = encodeURIComponent(JSON.stringify(displayedFilters));
      const path = `/orders?displayedFilters=${encodedDisplayedFilters}&filter=${encodedFilter}&order=DESC&page=1&perPage=10&sort=orderDate`;
      navigate(path);
    } catch (error) {
      console.error('注文ページへの遷移に失敗しました:', error);
    }
  };

  useEffect(() => {
    // パートナーダッシュボード（selectedResultType === 4）かつ未確定デバッグ用のログ出力
    if (selectedResultType !== 4) return;

    const fetchAndLogPendingOrders = async () => {
      try {
        let finalStartDate = startDate;
        let finalEndDate = endDate;
        if (dateType === 1) {
          finalStartDate = dayjs(startDate).startOf('month').format('YYYY-MM-DD');
          finalEndDate = dayjs(endDate).endOf('month').format('YYYY-MM-DD');
        }

        const filter: { [key: string]: any } = {
          approvalStatus: 0,
          orderDate_start: finalStartDate,
          orderDate_end: finalEndDate,
          hierarchyPattern: hierarchyPattern || 'all'
        };
        if (couponFlag === 1) {
          filter.hasCoupon = true;
        } else if (couponFlag === 2) {
          filter.hasCoupon = false;
        }

        // dataProviderを使って、集計対象の未確定（保留中）注文一覧を全件フェッチ
        const response = await dataProvider.getList('orders', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'orderDate', order: 'ASC' },
          filter
        });

        if (response && response.data) {
          const loggedInPartnerId = Number(getLocalStorageId());
          let calculatedTotal = 0;

          console.log(
            `%c[ANALYTICS-BROWSER-DEBUG] 表示パートナー切り替え: "${hierarchyPattern || 'all'}" | 未確定報酬額の計算に使用された注文一覧 (件数: ${response.data.length}):`,
            'color: #0088cc; font-weight: bold; font-size: 11px;'
          );
          
          response.data.forEach((o: any) => {
            const isOwnOrder = Number(o.partnerId) === loggedInPartnerId;
            const partnerRewardVal = o.reward || 0; // o.rewardPrice
            const introducerRewardVal = o.introducerRewardPrice || 0;
            
            let contributedAmount = 0;
            let contributionType = '集計対象外';
            
            switch (hierarchyPattern) {
              case 'own': // 1次パートナー（自分のみ）
                if (isOwnOrder) {
                  contributedAmount = partnerRewardVal;
                  contributionType = '1次直接報酬';
                } else {
                  contributedAmount = introducerRewardVal;
                  contributionType = '2次紹介報酬';
                }
                break;
                
              case 'secondary_total': // 2次パートナー合計
                if (!isOwnOrder) {
                  contributedAmount = partnerRewardVal;
                  contributionType = '2次直接報酬';
                }
                break;
                
              case 'all': // 全体合計（親子全体）
                if (isOwnOrder) {
                  contributedAmount = partnerRewardVal;
                  contributionType = '1次直接報酬';
                } else {
                  contributedAmount = partnerRewardVal + introducerRewardVal;
                  contributionType = '2次直接報酬 + 1次紹介報酬';
                }
                break;
                
              default:
                if (hierarchyPattern && hierarchyPattern.startsWith('partner_')) {
                  const selectedId = Number(hierarchyPattern.replace('partner_', ''));
                  if (Number(o.partnerId) === selectedId) {
                    contributedAmount = partnerRewardVal;
                    contributionType = '2次直接報酬(個別)';
                  }
                }
                break;
            }
            
            calculatedTotal += contributedAmount;
            
            console.log(
              `  - 注文ID: ${o.orderId} | 日付: ${dayjs(o.orderDate).format('YYYY-MM-DD')} | パートナーID: ${o.partnerId || '不明'} | 売上: ${o.price.toLocaleString()}円 | 2次報酬: ${partnerRewardVal.toLocaleString()}円 | 1次紹介報酬: ${introducerRewardVal.toLocaleString()}円 | %c未確定への貢献額: ${contributedAmount.toLocaleString()}円 (${contributionType})`,
              contributedAmount > 0 ? 'color: #d9534f; font-weight: bold;' : 'color: #999;'
            );
          });
          
          console.log(
            `%c  [表示カード集計合計]: ${calculatedTotal.toLocaleString()}円`,
            'color: #0088cc; font-weight: bold; font-size: 12px;'
          );
        }
      } catch (error) {
        console.error('[ANALYTICS-BROWSER-DEBUG] 注文データの取得に失敗しました:', error);
      }
    };

    fetchAndLogPendingOrders();
  }, [dataProvider, hierarchyPattern, startDate, endDate, dateType, couponFlag, selectedResultType]);

  useEffect(() => {
    // 古くなったリクエストは破棄
    let isCancelled = false;

    const loadData = async () => {
      setIsLoading(true);
      try {
        let adjustedStartDate = startDate;
        let adjustedEndDate = endDate;

        if (dateType === 1) {
          const [startYear, startMonth] = startDate.split('-');
          adjustedStartDate = `${startYear}-${startMonth}-01`;
          const [endYear, endMonth] = endDate.split('-');
          adjustedEndDate = `${endYear}-${endMonth}-01`;
        }

        const reportData = await dataProvider.getReportData(
          adjustedStartDate,
          adjustedEndDate,
          dateType,
          selectedResultType,
          couponFlag
        );

        if (!isCancelled && reportData) {
          setData(reportData.data ?? []);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Error fetching data:', error);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };
    loadData();

    return () => {
      isCancelled = true;
    };
  }, [dataProvider, startDate, endDate, dateType, selectedResultType, couponFlag]);

  const handleInputChange =
    (
      setter: React.Dispatch<React.SetStateAction<string>>,
      relatedSetter?: React.Dispatch<React.SetStateAction<string>>,
      comparisonDate?: string,
      isStartDate?: boolean
    ) =>
      (newDate: Dayjs | null) => {
        if (!newDate) return;

        let formattedDate: string;

        if (dateType === 1) {
          // 月選択モードの場合
          if (isStartDate) {
            formattedDate = newDate.startOf('month').format('YYYY-MM-DD');
          } else {
            if (newDate.isSame(dayjs(), 'month')) {
              formattedDate = dayjs().format('YYYY-MM-DD');
            } else {
              formattedDate = newDate.endOf('month').format('YYYY-MM-DD');
            }
          }
        } else {
          // 日付選択モードの場合
          formattedDate = newDate.format('YYYY-MM-DD');
        }

        const minDate = '2024-05-01';
        const maxDate = dayjs().format('YYYY-MM-DD');

        if (formattedDate < minDate || formattedDate > maxDate) {
          setDateError(`日付は ${minDate} から ${maxDate} の間で入力してください。`);
        } else {
          setDateError(null);
          setter(formattedDate);

          if (relatedSetter && comparisonDate) {
            if (isStartDate) {
              if (new Date(formattedDate) > new Date(comparisonDate)) {
                relatedSetter(dateType === 1 ? newDate.endOf('month').format('YYYY-MM-DD') : formattedDate);
              }
            } else {
              if (new Date(formattedDate) < new Date(comparisonDate)) {
                relatedSetter(dateType === 1 ? newDate.startOf('month').format('YYYY-MM-DD') : formattedDate);
              }
            }
          }
        }
      };
  const handleButtonClick = (setter: React.Dispatch<React.SetStateAction<number>>, value: number) => () => {
    setter(value);
  };

  const handleNameFilterChange = async (event: SelectChangeEvent) => {
    const selectedId = event.target.value;
    setNameFilter(selectedId);
  };

  const handleSortingChange = (updaterOrValue: MRT_SortingState | ((old: MRT_SortingState) => MRT_SortingState)) => {
    setSorting((oldSorting) => (typeof updaterOrValue === 'function' ? updaterOrValue(oldSorting) : updaterOrValue));
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadCSV = () => {
    const headers = columns.map((col) => col.header);
    const rows = sortedData.map((row) =>
      columns
        .map((col) => {
          const value = row[col.accessorKey as keyof DataItem];
          if (col.accessorKey === 'salesAmountRate' && value !== undefined) {
            return `"${value}%"`;
          }
          return typeof value === 'number' ? `"${formatNumber(value)}"` : `"${value ?? ''}"`;
        })
        .join(',')
    );

    const csvContent = [headers.join(','), ...rows].join('\n');

    const bom = '\uFEFF';
    const encodedUri = encodeURI(`data:text/csv;charset=utf-8,${bom}${csvContent}`);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `アナリティクス(${startDate}~${endDate}).csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredTableData = useMemo(() => {
    if (selectedResultType === 4 && hasChildPartners) {
      return data.map((entry) => {
        const ownItems = entry.items.filter((item) => item.affiliateOwnerType === 2);
        const secondaryItems = entry.items.filter((item) => item.affiliateOwnerType === 3);

        const getSalesAmount = (items: DataItem[]) => {
          const total = items.reduce((acc, item) => acc + (item.salesAmount ?? 0), 0);
          const coupon = items.reduce((acc, item) => acc + (item.salesAmountWithCoupon ?? 0), 0);
          if (couponFlag === 1) return coupon;
          if (couponFlag === 2) return total - coupon;
          return total;
        };

        const totalSalesAmountForDate = getSalesAmount(entry.items);

        // 特定の2次パートナー個別
        if (hierarchyPattern.startsWith('partner_')) {
          const selectedId = Number(hierarchyPattern.replace('partner_', ''));
          const targetPartnerItems = entry.items.filter((item) => item.partnerId === selectedId);
          const currentSales = getSalesAmount(targetPartnerItems);
          const salesAmountRate = totalSalesAmountForDate
            ? ((currentSales / totalSalesAmountForDate) * 100).toFixed(2)
            : '0.00';

          return {
            id: `partner_${selectedId}`,
            name: targetPartnerItems.length > 0 ? targetPartnerItems[0].partnerName : '個別パートナー',
            date: entry.date,
            salesAmount: getSalesAmount(targetPartnerItems),
            salesCount: targetPartnerItems.reduce((acc, item) => acc + (item.salesCount ?? 0), 0),
            clickCount: targetPartnerItems.reduce((acc, item) => acc + (item.clickCount ?? 0), 0),
            salesAmountWithCoupon: targetPartnerItems.reduce((acc, item) => acc + (item.salesAmountWithCoupon ?? 0), 0),
            salesCountWithCoupon: targetPartnerItems.reduce((acc, item) => acc + (item.salesCountWithCoupon ?? 0), 0),
            feebackCommission: targetPartnerItems.reduce((acc, item) => acc + (item.feebackCommission ?? 0), 0),
            partnerCommission: targetPartnerItems.reduce((acc, item) => acc + (item.partnerCommission ?? 0), 0),
            introducerCommission: 0,
            totalAffiliateCommission: targetPartnerItems.reduce((acc, item) => acc + (item.partnerCommission ?? 0), 0),
            salesAmountRate: salesAmountRate,
            partnerName: targetPartnerItems.length > 0 ? targetPartnerItems[0].partnerName : '',
            introducerName: targetPartnerItems.length > 0 ? targetPartnerItems[0].introducerName : null,
            affiliateOwnerType: 3,
            pendingPartnerCommission: targetPartnerItems.reduce((acc, item) => acc + (item.pendingPartnerCommission ?? 0), 0),
            pendingIntroducerCommission: 0
          };
        }

        switch (hierarchyPattern) {
          case 'own': // 自ら単体
            const ownSales = getSalesAmount(ownItems);
            const ownSalesAmountRate = totalSalesAmountForDate
              ? ((ownSales / totalSalesAmountForDate) * 100).toFixed(2)
              : '0.00';
            return {
              id: 'own_all',
              name: '1次パートナー',
              date: entry.date,
              salesAmount: ownSales,
              salesCount: ownItems.reduce((acc, item) => acc + (item.salesCount ?? 0), 0),
              clickCount: ownItems.reduce((acc, item) => acc + (item.clickCount ?? 0), 0),
              salesAmountWithCoupon: ownItems.reduce((acc, item) => acc + (item.salesAmountWithCoupon ?? 0), 0),
              salesCountWithCoupon: ownItems.reduce((acc, item) => acc + (item.salesCountWithCoupon ?? 0), 0),
              feebackCommission: ownItems.reduce((acc, item) => acc + (item.feebackCommission ?? 0), 0),
              partnerCommission: ownItems.reduce((acc, item) => acc + (item.partnerCommission ?? 0), 0),
              introducerCommission: secondaryItems.reduce((acc, item) => acc + (item.introducerCommission ?? 0), 0),
              totalAffiliateCommission: ownItems.reduce((acc, item) => acc + (item.partnerCommission ?? 0), 0) + secondaryItems.reduce((acc, item) => acc + (item.introducerCommission ?? 0), 0),
              salesAmountRate: ownSalesAmountRate,
              partnerName: '1次パートナー',
              introducerName: null,
              affiliateOwnerType: 2,
              pendingPartnerCommission: ownItems.reduce((acc, item) => acc + (item.pendingPartnerCommission ?? 0), 0),
              pendingIntroducerCommission: secondaryItems.reduce((acc, item) => acc + (item.pendingIntroducerCommission ?? 0), 0)
            };

          case 'secondary_total': // 2次パートナー合計
            const secondarySales = getSalesAmount(secondaryItems);
            const secondarySalesAmountRate = totalSalesAmountForDate
              ? ((secondarySales / totalSalesAmountForDate) * 100).toFixed(2)
              : '0.00';
            return {
              id: 'secondary_all',
              name: '2次パートナー合計',
              date: entry.date,
              salesAmount: getSalesAmount(secondaryItems),
              salesCount: secondaryItems.reduce((acc, item) => acc + (item.salesCount ?? 0), 0),
              clickCount: secondaryItems.reduce((acc, item) => acc + (item.clickCount ?? 0), 0),
              salesAmountWithCoupon: secondaryItems.reduce((acc, item) => acc + (item.salesAmountWithCoupon ?? 0), 0),
              salesCountWithCoupon: secondaryItems.reduce((acc, item) => acc + (item.salesCountWithCoupon ?? 0), 0),
              feebackCommission: secondaryItems.reduce((acc, item) => acc + (item.feebackCommission ?? 0), 0),
              partnerCommission: secondaryItems.reduce((acc, item) => acc + (item.partnerCommission ?? 0), 0),
              introducerCommission: 0,
              totalAffiliateCommission: secondaryItems.reduce((acc, item) => acc + (item.partnerCommission ?? 0), 0),
              salesAmountRate: secondarySalesAmountRate,
              partnerName: '2次パートナー合計',
              introducerName: null,
              affiliateOwnerType: 3,
              pendingPartnerCommission: secondaryItems.reduce((acc, item) => acc + (item.pendingPartnerCommission ?? 0), 0),
              pendingIntroducerCommission: 0
            };

          case 'all': // 親子全体の合計
          default:
            return {
              id: 'all_combined',
              name: '全体合計',
              date: entry.date,
              salesAmount: totalSalesAmountForDate,
              salesCount: entry.items.reduce((acc, item) => acc + (item.salesCount ?? 0), 0),
              clickCount: entry.items.reduce((acc, item) => acc + (item.clickCount ?? 0), 0),
              salesAmountWithCoupon: entry.items.reduce((acc, item) => acc + (item.salesAmountWithCoupon ?? 0), 0),
              salesCountWithCoupon: entry.items.reduce((acc, item) => acc + (item.salesCountWithCoupon ?? 0), 0),
              feebackCommission: entry.items.reduce((acc, item) => acc + (item.feebackCommission ?? 0), 0),
              partnerCommission: entry.items.reduce((acc, item) => acc + (item.partnerCommission ?? 0), 0),
              introducerCommission: entry.items.reduce((acc, item) => acc + (item.introducerCommission ?? 0), 0),
              totalAffiliateCommission: entry.items.reduce(
                (acc, item) => acc + (item.partnerCommission ?? 0) + (item.introducerCommission ?? 0),
                0
              ),
              salesAmountRate: totalSalesAmountForDate ? '100.00' : '0.00',
              partnerName: '全体合計',
              introducerName: null,
              affiliateOwnerType: 1,
              pendingPartnerCommission: entry.items.reduce((acc, item) => acc + (item.pendingPartnerCommission ?? 0), 0),
              pendingIntroducerCommission: entry.items.reduce((acc, item) => acc + (item.pendingIntroducerCommission ?? 0), 0)
            };
        }
      });
    }

    if (!nameFilter || nameFilter === '全体') {
      return data.map((entry) => {
        const totalSalesAmountForDate = entry.items.reduce((acc, item) => acc + (item.salesAmount ?? 0), 0);
        const totalSalesAmountWithCouponForDate = entry.items.reduce(
          (acc, item) => acc + (item.salesAmountWithCoupon ?? 0),
          0
        );

        let finalSalesAmount = totalSalesAmountForDate;
        if (couponFlag === 1) {
          // クーポンあり
          finalSalesAmount = totalSalesAmountWithCouponForDate;
        } else if (couponFlag === 2) {
          // クーポンなし
          finalSalesAmount = totalSalesAmountForDate - totalSalesAmountWithCouponForDate;
        }

        const partnerCommission = entry.items.reduce((acc, item) => acc + (item.partnerCommission ?? 0), 0);
        const introducerCommission = (selectedResultType === 4 && !hasChildPartners)
          ? 0
          : entry.items.reduce((acc, item) => acc + (item.introducerCommission ?? 0), 0);
        const totalAffiliateCommission = (selectedResultType === 4 && !hasChildPartners)
          ? partnerCommission
          : entry.items.reduce(
              (acc, item) => acc + (item.partnerCommission ?? 0) + (item.introducerCommission ?? 0),
              0
            );

        return {
          id: '',
          name: '全体',
          date: entry.date,
          salesAmount: finalSalesAmount,
          salesCount: entry.items.reduce((acc, item) => acc + (item.salesCount ?? 0), 0),
          salesAmountWithCoupon: totalSalesAmountWithCouponForDate,
          salesCountWithCoupon: entry.items.reduce((acc, item) => acc + (item.salesCountWithCoupon ?? 0), 0),
          feebackCommission: entry.items.reduce((acc, item) => acc + (item.feebackCommission ?? 0), 0),
          partnerCommission,
          introducerCommission,
          totalAffiliateCommission,
          clickCount: entry.items.reduce((acc, item) => acc + (item.clickCount ?? 0), 0),
          partnerName: '',
          introducerName: null,
          affiliateOwnerType: 0,
          pendingPartnerCommission: (selectedResultType === 4 && !hasChildPartners)
            ? entry.items.reduce((acc, item) => acc + (item.pendingPartnerCommission ?? 0), 0)
            : 0,
          pendingIntroducerCommission: 0
        };
      });
    }
    // 特定の項目が選択された場合のロジック
    return data.map((entry) => {
      // その日のitemsの中から、選択したnameFilterに一致するものだけを抽出
      const filteredItems = entry.items.filter((item) => item.id === nameFilter);
      // 【分母】: その日の全体の総売上（nameFilterで絞らない）
      const totalSalesAmountForDate = entry.items.reduce((acc, item) => acc + item.salesAmount, 0);
      // nameFilterで絞った総売上
      const totalSalesAmountForNameFilter = filteredItems.reduce((acc, item) => acc + item.salesAmount, 0);
      // クーポン利用売上
      const totalSalesAmountForCouponFilter = filteredItems.reduce((acc, item) => acc + item.salesAmountWithCoupon, 0);
      // 分子の値をcouponFlagに応じて動的に決定
      let numerator = totalSalesAmountForNameFilter; // デフォルトはnameFilterで絞った総売上

      if (couponFlag === 1) {
        // 「クーポンあり」の場合
        numerator = totalSalesAmountForCouponFilter;
      } else if (couponFlag === 2) {
        // 「クーポンなし」の場合
        numerator = totalSalesAmountForNameFilter - totalSalesAmountForCouponFilter;
      }

      // 割合を計算
      const salesAmountRate = totalSalesAmountForDate
        ? ((numerator / totalSalesAmountForDate) * 100).toFixed(2)
        : '0.00';
      const partnerCommission = filteredItems.reduce((acc, item) => acc + (item.partnerCommission ?? 0), 0);
      const introducerCommission = (selectedResultType === 4 && !hasChildPartners)
        ? 0
        : filteredItems.reduce((acc, item) => acc + (item.introducerCommission ?? 0), 0);
      const totalAffiliateCommission = (selectedResultType === 4 && !hasChildPartners)
        ? partnerCommission
        : filteredItems.reduce(
            (acc, item) => acc + (item.partnerCommission ?? 0) + (item.introducerCommission ?? 0),
            0
          );

      return {
        id: filteredItems.length > 0 ? filteredItems[0].id : '',
        name: nameFilter,
        date: entry.date,
        salesAmount: numerator,
        salesCount: filteredItems.reduce((acc, item) => acc + item.salesCount, 0),
        clickCount: filteredItems.reduce((acc, item) => acc + item.clickCount, 0),
        salesAmountWithCoupon: totalSalesAmountForCouponFilter,
        salesCountWithCoupon: filteredItems.reduce((acc, item) => acc + item.salesCountWithCoupon, 0),
        feebackCommission: filteredItems.reduce((acc, item) => acc + item.feebackCommission, 0),
        partnerCommission,
        introducerCommission,
        totalAffiliateCommission,
        salesAmountRate: salesAmountRate,
        partnerName: filteredItems.length > 0 ? filteredItems[0].partnerName : '',
        introducerName: filteredItems.length > 0 ? filteredItems[0].introducerName : null,
        affiliateOwnerType: filteredItems.length > 0 ? filteredItems[0].affiliateOwnerType : 0,
        pendingPartnerCommission: (selectedResultType === 4 && !hasChildPartners)
          ? filteredItems.reduce((acc, item) => acc + (item.pendingPartnerCommission ?? 0), 0)
          : 0,
        pendingIntroducerCommission: 0
      };
    });
  }, [data, nameFilter, couponFlag, selectedResultType, hasChildPartners, hierarchyPattern]);

  const filteredGraphData = useMemo(() => {
    return filteredTableData.map((row) => {
      const adjustedSalesAmount = Math.floor(
        (row.salesAmount ?? 0) - (row.feebackCommission ?? 0) - (row.totalAffiliateCommission ?? 0)
      );

      // 棒グラフ計算ようにマイナスの値を用意
      const negativeFeebackCommission = -(row.feebackCommission ?? 0);
      const negativeAffiliateCommission = -(row.totalAffiliateCommission ?? 0);

      return {
        date: formatDate(row.date, dateType),
        salesAmount: row.salesAmount ?? 0,
        adjustedSalesAmount: adjustedSalesAmount,
        feebackCommission: row.feebackCommission ?? 0,
        affiliateCommission: row.totalAffiliateCommission ?? 0,
        negativeFeebackCommission: negativeFeebackCommission,
        negativeAffiliateCommission: negativeAffiliateCommission
      };
    });
  }, [filteredTableData, dateType]);

  const cumulativeData = useMemo(() => {
    let cumulativeAffiliateCommission = 0;

    // dataではなくfilteredTableDataを元に計算する
    return filteredTableData.map((row) => {
      cumulativeAffiliateCommission += row.totalAffiliateCommission ?? 0;

      return {
        date: formatDate(row.date, dateType),
        affiliateCommission: integerNumber(cumulativeAffiliateCommission)
      };
    });
  }, [filteredTableData, dateType]);

  const displaySummary = useMemo(() => {
    const summary = filteredTableData.reduce(
      (acc, row) => {
        acc.totalClickCount += row.clickCount ?? 0;
        acc.totalSalesCount += row.salesCount ?? 0;
        acc.totalSalesAmount += row.salesAmount ?? 0;
        acc.totalFeebackCommission += row.feebackCommission ?? 0;
        acc.totalPartnerCommission += row.partnerCommission ?? 0;
        acc.totalIntroducerCommission += row.introducerCommission ?? 0;
        acc.totalAffiliateCommission += row.totalAffiliateCommission ?? 0;
        return acc;
      },
      {
        totalClickCount: 0,
        totalSalesCount: 0,
        totalSalesAmount: 0,
        totalFeebackCommission: 0,
        totalPartnerCommission: 0,
        totalIntroducerCommission: 0,
        totalAffiliateCommission: 0
      }
    );

    // successtee売上 = 手数料収益 + パートナー支払額(パートナー報酬 + 紹介報酬)
    const totalFeebackClaim = Math.floor(summary.totalFeebackCommission) + Math.floor(summary.totalPartnerCommission) + Math.floor(summary.totalIntroducerCommission);

    const totalPendingAmount = data
      .flatMap((entry) => entry.items)
      .reduce((acc, item) => {
        // nameFilter の適用 (selectedResultType = 2, 3 の時のみ。selectedResultType = 4 では nameFilter は常に全体)
        if (selectedResultType !== 4 && nameFilter !== '全体' && item.id !== nameFilter) {
          return acc;
        }

        // selectedResultType = 4 (パートナー画面) の場合
        if (selectedResultType === 4) {
          if (hasChildPartners) {
            switch (hierarchyPattern) {
              case 'own': // 1次パートナー（自分のみ）
                // 1次パートナー自身のアフィリエイトリンクから直接発生した未確定報酬と、2次パートナーからの紹介者報酬
                const ownDirectPending = item.affiliateOwnerType === 2 ? (item.pendingPartnerCommission || 0) : 0;
                const secondaryIntroPending = item.affiliateOwnerType === 3 ? (item.pendingIntroducerCommission || 0) : 0;
                return acc + ownDirectPending + secondaryIntroPending;

              case 'secondary_total': // 2次パートナー合計
                // すべての2次パートナー自身が得る予定の未確定報酬のみ
                const secondaryDirectPending = item.affiliateOwnerType === 3 ? (item.pendingPartnerCommission || 0) : 0;
                return acc + secondaryDirectPending;

              case 'all': // 全体合計（親子全体）
                return acc + (item.pendingPartnerCommission || 0) + (item.pendingIntroducerCommission || 0);

              default:
                if (hierarchyPattern.startsWith('partner_')) {
                  // 2次パートナー個別（特定のパートナーA等）
                  // その特定の2次パートナー自身が得る予定の未確定報酬のみ
                  const selectedId = Number(hierarchyPattern.replace('partner_', ''));
                  if (item.partnerId !== selectedId) return acc;
                  return acc + (item.pendingPartnerCommission || 0);
                }
                break;
            }
          } else {
            // 子パートナーを持たない場合 (ログインユーザーが2次パートナー自身など)
            // 自分の報酬のみを加算
            return acc + (item.pendingPartnerCommission || 0);
          }
        }

        // それ以外 (Admin/Client用。selectedResultType = 2, 3)
        return acc + (item.pendingPartnerCommission || 0) + (item.pendingIntroducerCommission || 0);
      }, 0);

    return { ...summary, totalFeebackClaim, totalPendingAmount };
  }, [filteredTableData, data, nameFilter]);

  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: 'date', desc: true } // 初期ソートを適切に設定（例: date）
  ]);

  const sortedData = useMemo(() => {
    let sorted = [...filteredTableData];
    sorting.forEach(({ id, desc }) => {
      sorted = sorted.sort((a, b) => {
        const aValue = a[id as keyof DataItem];
        const bValue = b[id as keyof DataItem];

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return desc ? bValue - aValue : aValue - bValue;
        } else if (typeof aValue === 'string' && typeof bValue === 'string') {
          return desc ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
        }
        return 0;
      });
    });
    return sorted;
  }, [filteredTableData, sorting]);

  // 共通で使うツールチップ文言
  const visitCountTooltipText = "訪問数はカートの仕様やブラウザ環境により計測されない場合があります。";

  const columns = useMemo<MRT_ColumnDef<DataItem>[]>(() => {
    const dateColumn: MRT_ColumnDef<DataItem> = {
      header: translate('resources.sales.fields.date') || '',
      accessorKey: 'date',
      size: 0,
      Cell: ({ cell }: { cell: MRT_Cell<DataItem> }) => {
        const dateValue = cell.getValue<string>();
        const formattedDate = formatDate(dateValue, dateType);
        return (
          <>
            {isSmallScreen && (
              <Typography variant="caption">
                {translate('resources.sales.fields.date')}
                <br />
              </Typography>
            )}
            {formattedDate}
          </>
        );
      }
    };

    const salesAmountColumn: MRT_ColumnDef<DataItem> = {
      header: translate('resources.sales.fields.salesAmount') || '',
      accessorKey: 'salesAmount',
      size: 0,
      Cell: ({ cell }: { cell: MRT_Cell<DataItem> }) => (
        <>
          {isSmallScreen && (
            <Typography variant="caption">
              {translate('resources.sales.fields.salesAmount')}
              <br />
            </Typography>
          )}
          {`${formatNumber(cell.getValue<number>())} ${translate('values.graphTargetUnits.salesAmount')}`}
        </>
      )
    };

    const salesCountColumn: MRT_ColumnDef<DataItem> = {
      header: translate('resources.sales.fields.salesCount') || '',
      accessorKey: 'salesCount',
      size: 0,
      Cell: ({ cell }: { cell: MRT_Cell<DataItem> }) => (
        <>
          {isSmallScreen && (
            <Typography variant="caption">
              {translate('resources.sales.fields.salesCount')}
              <br />
            </Typography>
          )}
          {`${formatNumber(cell.getValue<number>())} ${translate('values.graphTargetUnits.salesCount')}`}
        </>
      )
    };

    const salesAmountRateColumn: MRT_ColumnDef<DataItem> = {
      header: translate('resources.sales.fields.salesAmountRate') || '',
      accessorKey: 'salesAmountRate',
      size: 0,
      Cell: ({ cell }: { cell: MRT_Cell<DataItem> }) => (
        <>
          {isSmallScreen && (
            <Typography variant="caption">
              {translate('resources.sales.fields.salesAmountRate')}
              <br />
            </Typography>
          )}
          {`${cell.getValue<string>()}%`}
        </>
      )
    };

    const feebackCommissionColumn: MRT_ColumnDef<DataItem> = {
      header: translate('resources.sales.fields.feebackCommission') || '',
      accessorKey: 'feebackCommission',
      size: 0,
      Cell: ({ cell }: { cell: MRT_Cell<DataItem> }) => (
        <>
          {isSmallScreen && (
            <Typography variant="caption">
              {translate('resources.sales.fields.feebackCommission')}
              <br />
            </Typography>
          )}
          {`${formatNumber(cell.getValue<number>())} ${translate('values.graphTargetUnits.feebackCommission')}`}
        </>
      )
    };

    const totalAffiliateCommissionColumn: MRT_ColumnDef<DataItem> = {
      header: translate('resources.sales.fields.affiliateCommission') || '',
      accessorKey: 'totalAffiliateCommission',
      size: 0,
      Cell: ({ cell }: { cell: MRT_Cell<DataItem> }) => {
        const item = cell.row.original;
        const shouldShowTooltip = selectedResultType === 4;
        return (
          <>
            {isSmallScreen && (
              <Typography variant="caption">
                {translate('resources.sales.fields.affiliateCommission')}
                <br />
              </Typography>
            )}
            {shouldShowTooltip ? (
              <MuiTooltip title={<CustomTableTooltip item={item} />}>
                <span>{`${formatNumber(item.totalAffiliateCommission ?? 0)} ${translate(
                  'values.graphTargetUnits.affiliateCommission'
                )}`}</span>
              </MuiTooltip>
            ) : (
              <span>{`${formatNumber(item.totalAffiliateCommission ?? 0)} ${translate(
                'values.graphTargetUnits.affiliateCommission'
              )}`}</span>
            )}
          </>
        );
      }
    };

    const clickCountColumn: MRT_ColumnDef<DataItem> = {
      header: translate('resources.sales.fields.clickCount') || '',
      accessorKey: 'clickCount',
      size: 0,
      Header: ({ column }) => (
        <Box display="flex" alignItems="center">
          {column.columnDef.header}
          <TouchTooltipIcon title={visitCountTooltipText} />
        </Box>
      ),
      Cell: ({ cell }: { cell: MRT_Cell<DataItem> }) => (
        <>
          {isSmallScreen && (
            <Box display="flex" alignItems="center">
              <Typography variant="caption">
                {translate('resources.sales.fields.clickCount')}
              </Typography>
              <TouchTooltipIcon title={visitCountTooltipText} fontSize="0.8rem" />
              <br />
            </Box>
          )}
          {`${formatNumber(cell.getValue<number>())} ${translate('values.graphTargetUnits.clickCount')}`}
        </>
      )
    };

    // カラムを必要な順番に並べる
    let columnsList: MRT_ColumnDef<DataItem>[] = [];

    if (selectedResultType === 4) {
      // selectedResultType が 4 の場合
      if (hasChildPartners && hierarchyPattern !== 'all') {
        columnsList = [
          dateColumn,
          totalAffiliateCommissionColumn,
          salesAmountColumn,
          salesCountColumn,
          clickCountColumn,
          salesAmountRateColumn
        ];
      } else {
        columnsList = [
          dateColumn,
          totalAffiliateCommissionColumn,
          salesAmountColumn,
          salesCountColumn,
          clickCountColumn
        ];
      }
    } else if (nameFilter !== '全体' && selectedResultType !== 4) {
      // nameFilter が '全体' でなく、かつ selectedResultType が 4 でない場合
      columnsList = [
        dateColumn,
        salesAmountColumn,
        salesCountColumn,
        feebackCommissionColumn,
        totalAffiliateCommissionColumn,
        clickCountColumn,
        salesAmountRateColumn
      ];
    } else {
      // それ以外の場合
      columnsList = [
        dateColumn,
        salesAmountColumn,
        salesCountColumn,
        feebackCommissionColumn,
        totalAffiliateCommissionColumn,
        clickCountColumn
      ];
    }

    return columnsList;
  }, [selectedResultType, translate, isSmallScreen, dateType, nameFilter, hasChildPartners, hierarchyPattern]);
  // console.log('レンダー直前のデータ:', data);


  // 1列分のハイライト領域を計算して描画するカーソル
  const CustomBarCursor = (props: any) => {
    // ComposedChartでは x ではなく points[0].x または coordinate.x に値が入る
    const currentX = props.x ?? props.points?.[0]?.x ?? props.coordinate?.x;
    const plotWidth = props.width ?? props.viewBox?.width ?? 0;
    const plotHeight = props.height ?? props.viewBox?.height ?? 0;
    const plotTop = props.top ?? props.viewBox?.y ?? 16;

    const dataLength = filteredGraphData?.length || 0;

    if (currentX === undefined || plotWidth === 0 || dataLength === 0) {
      return null;
    }

    // 1列分の幅を算出
    const bandWidth = plotWidth / dataLength;

    return (
      <rect
        x={currentX - bandWidth / 2}
        y={plotTop}
        width={bandWidth}
        height={plotHeight}
        fill='#f5f5f5'
        pointerEvents="none"
      />
    );
  };

  return (
    <Card sx={{ pl: 2, pr: 2, pt: 1, boxShadow: 'none' }}>
      <Title title={translate('resources.sales.name.sales')} />
      <Stack mt={1}>
        <Stack>
          <Stack display="flex" flexDirection="column" gap={0}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              指定期間内の実績
            </Typography>
            <Stack display="flex" flexDirection={isSmallScreen ? 'column' : 'row'} gap={2} sx={{ margin: '0 0 0 0' }}>
              <Stack display="flex" flexDirection={'row'} justifyContent="left" alignItems="flex-start" gap={2}>
                <Box display="flex" alignItems="flex-start" gap={0.5}>
                  <DatePicker
                    label={
                      dateType === 1
                        ? translate('resources.sales.fields.startMonth')
                        : translate('resources.sales.fields.startDate')
                    }
                    value={dayjs(startDate)}
                    onChange={handleInputChange(setStartDate, setEndDate, endDate, true)}
                    maxDate={dayjs()}
                    minDate={dayjs('2024-05-01')}
                    views={dateType === 1 ? ['year', 'month'] : ['year', 'month', 'day']}
                    format={dateType === 1 ? 'YYYY-MM' : 'YYYY-MM-DD'}
                    openTo={dateType === 1 ? 'month' : 'day'}
                    slotProps={{
                      textField: {
                        helperText: dateError,
                        error: !!dateError,
                        sx: {
                          maxWidth: 150,
                          '& .MuiInputBase-root': {
                            paddingLeft: '14px',
                            paddingRight: '14px'
                          },
                          '& .MuiInputAdornment-root': {
                            marginRight: 0
                          },
                          '& input': {
                            padding: '10px 0'
                          }
                        },
                        FormHelperTextProps: { sx: { whiteSpace: 'normal' } }
                      }
                    }}
                  />
                  <Typography variant="body1" mt={2}>
                    ～
                  </Typography>
                  <DatePicker
                    label={
                      dateType === 1
                        ? translate('resources.sales.fields.endMonth')
                        : translate('resources.sales.fields.endDate')
                    }
                    value={dayjs(endDate)}
                    onChange={handleInputChange(setEndDate, setStartDate, startDate, false)}
                    maxDate={dayjs()}
                    minDate={dayjs('2024-05-01')}
                    views={dateType === 1 ? ['year', 'month'] : ['year', 'month', 'day']}
                    format={dateType === 1 ? 'YYYY-MM' : 'YYYY-MM-DD'}
                    openTo={dateType === 1 ? 'month' : 'day'}
                    slotProps={{
                      textField: {
                        helperText: dateError,
                        error: !!dateError,
                        sx: {
                          maxWidth: 150,
                          '& .MuiInputBase-root': {
                            paddingLeft: '14px',
                            paddingRight: '14px'
                          },
                          '& .MuiInputAdornment-root': {
                            marginRight: 0
                          },
                          '& input': {
                            padding: '10px 0'
                          }
                        },
                        FormHelperTextProps: { sx: { whiteSpace: 'normal' } }
                      }
                    }}
                  />
                </Box>
                <Box display="flex" flexDirection="row" alignItems="center" mt={1} gap={3}>
                  <ButtonGroup
                    variant="outlined"
                    aria-label="outlined button group"
                    size={isSmallScreen ? 'small' : 'large'}
                    // orientation={isSmallScreen ? 'vertical' : 'horizontal'} // スクリーンサイズによってレイアウトを変更
                    sx={{ height: '51px' }}
                  >
                    {ANALYSIS_DATE_TYPE.map((type) => (
                      <Button
                        key={type.id}
                        onClick={handleButtonClick(setDateType, type.id)}
                        variant={dateType === type.id ? 'contained' : 'outlined'}
                        sx={(theme) => ({
                          border: `2px solid ${theme.palette.primary.main}`,
                          '&:hover': {
                            border: `2px solid ${theme.palette.primary.main}`
                          }
                        })}
                      >
                        {type.name}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>
              </Stack>
              {(selectedResultType === 2 || selectedResultType === 3) && (
                <Box>
                  <FormControl>
                    <InputLabel id="name-select-label">
                      {selectedResultType === 2
                        ? translate('resources.sales.fields.selectClient')
                        : selectedResultType === 3
                          ? translate('resources.sales.fields.selectPartner')
                          : translate('resources.sales.fields.name')}
                    </InputLabel>
                    <Select
                      labelId="name-select-label"
                      label={
                        selectedResultType === 2
                          ? translate('resources.sales.fields.selectClient')
                          : selectedResultType === 3
                            ? translate('resources.sales.fields.selectPartner')
                            : translate('resources.sales.fields.name')
                      }
                      value={nameFilter}
                      onChange={handleNameFilterChange}
                      sx={{
                        minWidth: { xs: '140px', sm: '140px', md: '140px' },
                        maxWidth: { xs: '280px', sm: '310px', md: '365px' }
                      }}
                    >
                      <MenuItem value="全体">全体</MenuItem>
                      {data
                        .flatMap((entry) => entry.items)
                        .filter((item, index, self) => self.findIndex((i) => i.id === item.id) === index)
                        .filter((item) => item.name !== '合計')
                        .map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {selectedResultType === 2 && item.id
                              ? `(${item.id}) ${item.name}` // ECサイト別の場合は (ID) 名称
                              : item.name                   // パートナー別の場合は 名称のみ
                            }
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              {selectedResultType === 4 && hasChildPartners && (
                <Box>
                  <FormControl sx={{ minWidth: 240 }}>
                    <InputLabel id="hierarchy-pattern-select-label">パートナー選択</InputLabel>
                    <Select
                      labelId="hierarchy-pattern-select-label"
                      id="hierarchy-pattern-select"
                      value={hierarchyPattern}
                      label="パートナー選択"
                      onChange={(e) => setHierarchyPattern(e.target.value as HierarchyPattern)}
                    >
                      <MenuItem value="all">全体</MenuItem>
                      <MenuItem value="own">1次パートナー</MenuItem>
                      <MenuItem value="secondary_total">2次パートナー合計</MenuItem>
                      
                      {secondaryPartnersList.length > 0 && [
                        <ListSubheader key="subheader">2次パートナー個別</ListSubheader>,
                        secondaryPartnersList.map((partner) => (
                          <MenuItem key={partner.id} value={`partner_${partner.id}`}>
                            {partner.name}
                          </MenuItem>
                        ))
                      ]}
                    </Select>
                  </FormControl>
                </Box>
              )}
              <Box>
                <FormControl>
                  <InputLabel id="coupon-flag-select-label">クーポン</InputLabel>
                  <Select
                    labelId="coupon-flag-select-label"
                    id="coupon-flag-select"
                    value={couponFlag}
                    label="クーポン"
                    onChange={(event: SelectChangeEvent<number>) => {
                      setCouponFlag(Number(event.target.value));
                    }}
                  >
                    <MenuItem key={0} value={0}>
                      全て
                    </MenuItem>
                    {COUPON_FLAG.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </Stack>
          {/* --- パターンA: Admin (isAdmin) --- */}
          <Stack sx={{ margin: '0' }}>
            {(selectedResultType === 2 || selectedResultType === 3) && isAdmin && (
              <Stack sx={{ my: 0 }}>
                {/* 1行目 */}
                <Grid container>
                  <SummaryItem
                    label={translate('resources.sales.fields.totalClickCount')}
                    value={displaySummary.totalClickCount}
                    dataKey="totalClickCount"
                    gridProps={{ xs: 6, sm: 3 }}
                    tooltipText={visitCountTooltipText}
                  />
                  <SummaryItem
                    label={translate('resources.sales.fields.totalSalesCount')}
                    value={displaySummary.totalSalesCount}
                    dataKey="totalSalesCount"
                    gridProps={{ xs: 6, sm: 3 }}
                  />
                  <ClickableSummaryItem
                    label={translate('resources.sales.fields.totalPendingAmount')}
                    value={displaySummary.totalPendingAmount}
                    onClick={handleNavigateToOrders}
                    gridProps={{ xs: 6, sm: 3 }}
                  />
                </Grid>
                {/* 2行目 */}
                <Grid container>
                  <SummaryItem
                    label={translate('resources.sales.fields.totalSalesAmount')}
                    value={displaySummary.totalSalesAmount}
                    dataKey="totalSalesAmount"
                    gridProps={{ xs: 6, sm: 3 }}
                  />
                  <SummaryItem
                    label={translate('resources.sales.fields.totalFeebackClaim')}
                    value={displaySummary.totalFeebackClaim}
                    dataKey="totalFeebackClaim"
                    gridProps={{ xs: 6, sm: 3 }}
                  />
                  <SummaryItem
                    label={translate('resources.sales.fields.totalFeebackCommission')}
                    value={displaySummary.totalFeebackCommission}
                    dataKey="totalFeebackCommission"
                    gridProps={{ xs: 6, sm: 3 }}
                  />
                  <SummaryItem
                    label={translate('resources.sales.fields.totalAffiliateCommission')}
                    value={displaySummary.totalAffiliateCommission}
                    dataKey="totalAffiliateCommission"
                    gridProps={{ xs: 6, sm: 3 }}
                  />
                  {shouldShowBuyerCount && (
                    <ClickableSummaryItem
                      label={translate('resources.sales.fields.activeBuyerCount')}
                      value={activeBuyerCount}
                      onClick={handleNavigateToBuyers}
                      gridProps={{ xs: 6, sm: 3 }}
                      unitTranslationKey="values.graphTargetUnits.buyerCount"
                    />
                  )}
                </Grid>
              </Stack>
            )}

            {/* --- パターンB: Client (!isAdmin) --- */}
            {selectedResultType === 3 && !isAdmin && (
              <Stack sx={{ my: 0 }}>
                {/* 1行目 */}
                <Grid container>
                  <SummaryItem
                    label={translate('resources.sales.fields.totalClickCount')}
                    value={displaySummary.totalClickCount}
                    dataKey="totalClickCount"
                    gridProps={{ xs: 4 }}
                    tooltipText={visitCountTooltipText}
                  />
                  <SummaryItem
                    label={translate('resources.sales.fields.totalSalesCount')}
                    value={displaySummary.totalSalesCount}
                    dataKey="totalSalesCount"
                    gridProps={{ xs: 4 }}
                  />
                  <ClickableSummaryItem
                    label={translate('resources.sales.fields.totalPendingAmount')}
                    value={displaySummary.totalPendingAmount}
                    onClick={handleNavigateToOrders}
                    gridProps={{ xs: 4 }}
                  />
                </Grid>
                {/* 2行目 */}
                <Grid container>
                  <SummaryItem
                    label={translate('resources.sales.fields.totalSalesAmount')}
                    value={displaySummary.totalSalesAmount}
                    dataKey="totalSalesAmount"
                    gridProps={{ xs: 4 }}
                  />
                  <SummaryItem
                    label={translate('resources.sales.fields.totalFeebackCommission')}
                    value={displaySummary.totalFeebackCommission}
                    dataKey="totalFeebackCommission"
                    gridProps={{ xs: 4 }}
                  />
                  <SummaryItem
                    label={translate('resources.sales.fields.totalAffiliateCommission')}
                    value={displaySummary.totalAffiliateCommission}
                    dataKey="totalAffiliateCommission"
                    gridProps={{ xs: 4 }}
                  />
                  {shouldShowBuyerCount && (
                    <ClickableSummaryItem
                      label={translate('resources.sales.fields.activeBuyerCount')}
                      value={activeBuyerCount}
                      onClick={handleNavigateToBuyers}
                      gridProps={{ xs: 4 }}
                      unitTranslationKey="values.graphTargetUnits.buyerCount"
                    />
                  )}
                </Grid>
              </Stack>
            )}

            {/* --- パターンC: Partner (isAdmin) --- */}
            {selectedResultType === 4 && isAdmin && (
              <Stack sx={{ my: 0 }}>
                {/* 1行目 */}
                <Grid container>
                  <SummaryItem
                    label={translate('resources.sales.fields.totalClickCount')}
                    value={displaySummary.totalClickCount}
                    dataKey="totalClickCount"
                    gridProps={{ xs: 6, sm: 3 }}
                    tooltipText={visitCountTooltipText}
                  />
                  <SummaryItem
                    label={translate('resources.sales.fields.totalSalesCount')}
                    value={displaySummary.totalSalesCount}
                    dataKey="totalSalesCount"
                    gridProps={{ xs: 6, sm: 3 }}
                  />
                  <SummaryItem
                    label={translate('resources.sales.fields.totalAffiliateCommission')}
                    value={displaySummary.totalAffiliateCommission}
                    dataKey="totalAffiliateCommission"
                    gridProps={{ xs: 6, sm: 3 }}
                  />
                  <ClickableSummaryItem
                    label={translate('resources.sales.fields.totalPendingAmount')}
                    value={displaySummary.totalPendingAmount}
                    onClick={handleNavigateToOrders}
                    gridProps={{ xs: 6, sm: 3 }}
                  />
                  {shouldShowBuyerCount && (
                    <ClickableSummaryItem
                      label={translate('resources.sales.fields.activeBuyerCount')}
                      value={activeBuyerCount}
                      onClick={handleNavigateToBuyers}
                      gridProps={{ xs: 6, sm: 3 }}
                      unitTranslationKey="values.graphTargetUnits.buyerCount"
                    />
                  )}
                </Grid>
              </Stack>
            )}
          </Stack>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ResponsiveContainer height={isSmallScreen ? 200 : 350}>
              {selectedResultType === 4 ? (
                <AreaChart data={cumulativeData} margin={{ top: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(tick) => formatDate(tick, dateType)} />
                  <YAxis
                    width={80}
                    tickFormatter={(tick) =>
                      tick >= 1000000
                        ? `${(tick / 1000000).toLocaleString()}M ${translate(
                          'values.graphTargetUnits.affiliateCommission'
                        )}`
                        : `${tick.toLocaleString()} ${translate('values.graphTargetUnits.affiliateCommission')}`
                    }
                  />
                  <RechartsTooltip
                    content={<CustomGraphTooltip graphTarget={graphTarget} selectedResultType={selectedResultType} />}
                  />
                  <Legend
                    formatter={(value) => (
                      <span style={{ fontSize: isSmallScreen ? '0.8rem' : '1rem' }}>
                        {translate(`resources.sales.fields.${value}`)}
                      </span>
                    )}
                  />
                  <Area type="monotone" dataKey="affiliateCommission" stroke="#8884d8" fill="#8884d8" />
                </AreaChart>
              ) : (
                <ComposedChart data={filteredGraphData} margin={{ top: 16 }} stackOffset="sign">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(tick) => formatDate(tick, dateType)} />
                  <YAxis
                    width={80}
                    tickFormatter={(tick) =>
                      tick >= 1000000
                        ? `${(tick / 1000000).toLocaleString()}M ${translate(`values.graphTargetUnits.${graphTarget}`)}`
                        : `${tick.toLocaleString()} ${translate(`values.graphTargetUnits.${graphTarget}`)}`
                    }
                  />
                  <RechartsTooltip
                    cursor={<CustomBarCursor />}
                    content={<CustomGraphTooltip graphTarget={graphTarget} selectedResultType={selectedResultType} />}
                  />
                  <Legend
                    formatter={(value) => (
                      <span style={{ fontSize: isSmallScreen ? '0.8rem' : '1rem' }}>
                        {translate(`resources.sales.fields.${value}`)}
                      </span>
                    )}
                  />
                  {/* 日別の単体棒グラフ */}
                  {/* <Bar dataKey="salesAmount" stackId="unstacked-sales" fill={colorScale('salesAmount')} />
                  <Bar dataKey="feebackCommission" stackId="unstacked-fees" fill={colorScale('feebackCommission')} />
                  <Bar dataKey="affiliateCommission" stackId="unstacked-partner" fill={colorScale('affiliateCommission')} />
                  <Bar dataKey="adjustedSalesAmount" stackId="unstacked-adjusted-sales" fill={colorScale('adjustedSalesAmount')} /> */}
                  {/* 従来の縦積み棒グラフ */}
                  <Bar dataKey="salesAmount" stackId="a" fill={schemeCategory10[0]} />
                  <Bar dataKey="negativeAffiliateCommission" stackId="a" fill={schemeCategory10[2]} />
                  <Bar dataKey="negativeFeebackCommission" stackId="a" fill={schemeCategory10[1]} />
                  {/* 差引金額（手元売上）を折れ線グラフで重ねて表示 */}
                  <Line
                    type="linear"
                    dataKey="adjustedSalesAmount"
                    stroke={schemeCategory10[3]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 8 }}
                  />
                </ComposedChart>
              )}
            </ResponsiveContainer>
          )}
        </Stack>
        <Stack display="flex" flexDirection="column" gap={1} mt={1}>
          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
            <Box>
              {nameFilter && (
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  明細
                </Typography>
              )}
            </Box>
            {isSmallScreen ? (
              <Box display="flex" justifyContent="space-between">
                <Box>
                  <Button variant="outlined" onClick={handleClick}>
                    並び替え
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    sx={{ '& .MuiPaper-root': { bixShadow: 'none' } }}
                  >
                    {columns.map((column) => {
                      const isActive = sorting.find((s) => s.id === column.accessorKey);
                      const currentSortOrder = isActive?.desc ? 'desc' : 'asc';
                      return (
                        <MenuItem key={column.accessorKey}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <Typography
                              sx={{
                                mr: 2,
                                color: isActive ? 'blue' : 'inherit',
                                fontWeight: isActive ? 'bold' : 'normal'
                              }}
                            >
                              {column.header}
                            </Typography>
                            <ToggleButtonGroup
                              value={isActive ? currentSortOrder : null}
                              exclusive
                              onChange={(event, newOrder) => {
                                setSorting([{ id: column.accessorKey as string, desc: newOrder === 'desc' }]);
                                handleClose();
                              }}
                              size="small"
                              aria-label={`${column.header} ソート順`}
                            >
                              <ToggleButton
                                value="asc"
                                aria-label="昇順"
                                sx={{
                                  backgroundColor: isActive && currentSortOrder === 'asc' ? 'lightblue' : 'inherit',
                                  fontWeight: isActive && currentSortOrder === 'asc' ? 'bold' : 'normal'
                                }}
                              >
                                昇順
                              </ToggleButton>
                              <ToggleButton
                                value="desc"
                                aria-label="降順"
                                sx={{
                                  backgroundColor: isActive && currentSortOrder === 'desc' ? 'lightblue' : 'inherit',
                                  fontWeight: isActive && currentSortOrder === 'desc' ? 'bold' : 'normal'
                                }}
                              >
                                降順
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </Box>

                <Box>
                  <Button variant="text" color="primary" onClick={downloadCSV} startIcon={<DownloadIcon />}>
                    CSVダウンロード
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box marginLeft="auto">
                <Button
                  variant="text"
                  color="primary"
                  onClick={downloadCSV}
                  startIcon={<DownloadIcon />} // アイコンを追加
                >
                  CSVダウンロード
                </Button>
              </Box>
            )}
          </Box>
          <MaterialReactTable
            columns={columns}
            data={isShowingAll ? sortedData : sortedData.slice(0, 10)}
            enableSortingRemoval={false}
            enableSorting={true}
            state={{ sorting }}
            onSortingChange={setSorting}
            icons={{
              SyncAltIcon: () => null
            }}
            initialState={{
              density: 'compact'
            }}
            muiTablePaperProps={{
              elevation: 0,
              sx: {
                boxShadow: {
                  xs: 'none',
                  sm: 'none',
                  md: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)'
                }
              }
            }}
            muiTableHeadProps={{
              sx: {
                display: isSmallScreen ? 'none' : 'table-header-group',
                '& td': { padding: '12px 16px' },
                '& th': { padding: '12px 16px' }
              }
            }}
            muiTableBodyRowProps={{
              sx: isSmallScreen
                ? {
                  display: 'block',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  marginBottom: '2px',
                  padding: '2px',
                  backgroundColor: '#fff',
                  '& .MuiPaper-root': {
                    boxShadow: {
                      xs: 'none',
                      sm: 'none',
                      md: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)'
                    }
                  },
                  '& .css-hql76w-MuiTableCell-root': { borderBottom: 'none' },
                  '& .css-1xtblke-MuiTableCell-root': { borderBottom: 'none' }
                }
                : {
                  '& td': { padding: '12px 16px' },
                  '& th': { padding: '12px 16px' }
                },
              hover: true
            }}
            muiTableBodyCellProps={{
              sx: isSmallScreen
                ? {
                  display: 'block',
                  width: '100%',
                  padding: '0px 4px',
                  '&:not(:last-child)': {
                    borderBottom: 'none'
                  }
                }
                : {}
            }}
            localization={customLocalization}
            enableColumnActions={false}
            enablePagination={false}
            enableTopToolbar={false} // トップツールバーを非表示
            enableBottomToolbar={false}
          />

          {filteredTableData.length > 10 && (
            <Button onClick={() => setIsShowingAll(!isShowingAll)}>{isShowingAll ? '少なく表示' : 'さらに表示'}</Button>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

export default Sales;
